import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { PreQualForm } from "@/components/PreQualForm";
import { AreaChecker } from "@/components/AreaChecker";
import { SEOHead } from "@/components/SEOHead";
import { useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  useCarousel,
} from "@/components/ui/carousel";
import {
  CheckCircle2,
  TrendingUp,
  CalendarCheck,
  MessageSquare,
  BarChart3,
  Users,
  MapPin,
} from "lucide-react";

const CarouselIndicator = () => {
  const { api } = useCarousel();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <div className="flex gap-2">
      {Array.from({ length: count }).map((_, i) => (
        <button
          key={i}
          onClick={() => api?.scrollTo(i)}
          className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-bold transition-colors ${
            current === i + 1
              ? "bg-[#fe5240] text-white shadow-md"
              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
          }`}
        >
          {i + 1}
        </button>
      ))}
    </div>
  );
};

const Landing = () => {
  const scrollToBooking = () => {
    const el = document.getElementById("booking-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      {/* Minimal Header */}
      <header className="py-6 px-4 border-b border-slate-100 bg-white sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center max-w-5xl">
          <div className="flex items-center">
            <img
              src="https://vibe.filesafe.space/1775166810156919871/attachments/07b67583-88fd-417b-bb44-aeaaee1d2dda.png"
              alt="Clean Marketing Logo"
              className="h-10 object-contain"
            />
          </div>
          <Button
            onClick={scrollToBooking}
            className="bg-[#fe5240] hover:bg-[#e04825] text-white font-bold px-6 py-2 rounded-md transition-colors"
          >
            Book Your Demo
          </Button>
        </div>
      </header>

      <SEOHead
        title="Fill Your Vans With Window Cleaning Customers | Clean Marketing"
        description="More booked window cleaning jobs. Less wasted marketing spend. Built only for window cleaners. Book your demo and see how our system fills your rounds."
        canonical="/landing"
      />
      <main>
        {/* Hero Section */}
        <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 overflow-hidden bg-slate-50 min-h-[90svh] flex flex-col justify-center">
          <div className="absolute inset-0 z-0">
            <img
              src="https://vibe.filesafe.space/1775166810156919871/attachments/eb4534bf-7d5a-4dc0-b2f6-61b9ac5425a3.jpg"
              alt="Window cleaner at work"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-white/60 bg-gradient-to-b from-white/70 via-white/60 to-white/80"></div>
          </div>
          <div className="container mx-auto px-4 max-w-5xl text-center relative z-10 animate-fade-in">
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold text-[#004d8c] leading-[1.1] mb-6 tracking-tight drop-shadow-sm">
              Fill Your Vans With Regular Window Cleaning Customers —{" "}
              <span className="text-[#fe5240]">
                Without Pouring More Money Into Marketing That Doesn't Bring
                Bookings.
              </span>
            </h1>
            <p className="text-lg md:text-2xl text-slate-800 font-medium mb-10 max-w-3xl mx-auto leading-relaxed drop-shadow-sm">
              We run your ads and our proven booking system turns the enquiries
              into actual bookings — built by a window cleaner who's spent over
              £50k a month getting it right.
            </p>
            <Button
              onClick={scrollToBooking}
              className="bg-[#fe5240] hover:bg-[#e04825] text-white font-bold text-lg px-10 py-6 rounded-md shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
            >
              Book Your Demo
            </Button>
          </div>
        </section>

        {/* Trust Strip */}
        <section className="py-8 bg-[#004d8c] text-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-x divide-white/20">
              <div>
                <p className="text-3xl md:text-4xl font-bold mb-1">£2.7M+</p>
                <p className="text-sm text-blue-200">
                  Recurring Revenue Generated
                </p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold mb-1">36,000+</p>
                <p className="text-sm text-blue-200">Leads Processed</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold mb-1">6,500+</p>
                <p className="text-sm text-blue-200">Admin Hours Saved</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold mb-1">35+</p>
                <p className="text-sm text-blue-200">Active Clients</p>
              </div>
            </div>
          </div>
        </section>

        {/* The Problem */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#004d8c] mb-8">
                  Tired of paying for marketing that{" "}
                  <span className="text-[#fe5240]">
                    eats cash and brings nothing back?
                  </span>
                </h2>
                <div className="space-y-6 text-lg text-slate-700 leading-relaxed">
                  <p>
                    You're a window cleaner. You know the value of a good
                    customer. But if you're like most ambitious owners, you've
                    probably been burned before.
                  </p>
                  <p>
                    You've hired a local "marketing guru" or an agency that
                    promised the world. They took your money, ran some generic
                    ads, and sent you a bunch of useless leads. The leads either
                    didn't answer the phone, lived outside your area, or just
                    wanted a £10 clean.
                  </p>
                  <p>
                    Worse, you lost hours of your evenings chasing these
                    dead-end enquiries instead of resting or spending time with
                    your family. Marketing feels confusing, lonely, and like a
                    bottomless pit for your hard-earned cash.
                  </p>
                  <p className="font-bold text-[#004d8c] text-xl pt-4">
                    There is a better way.
                  </p>
                </div>
              </div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200 aspect-[4/3] lg:aspect-square">
                <img
                  src="https://vibe.filesafe.space/1775166810156919871/attachments/751503f6-bd4b-450a-ae95-5884cfeebbea.jpg"
                  alt="Frustrated business owner"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* The Solution */}
        <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-100">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004d8c] mb-6">
              The only marketing agency built{" "}
              <span className="text-[#fe5240]">for</span> window cleaners,{" "}
              <span className="text-[#fe5240]">by</span> a window cleaner.
            </h2>
            <p className="text-xl text-slate-700 mb-10 leading-relaxed">
              We bring you a steady flow of window cleaning customers and our
              system books them for you, so you can stay focused on running and
              growing your business.
            </p>
            <Button
              onClick={scrollToBooking}
              className="bg-[#fe5240] hover:bg-[#e04825] text-white font-bold text-lg px-10 py-6 rounded-md shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
            >
              Book Your Demo
            </Button>
          </div>
        </section>

        {/* How It Helps You */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004d8c] mb-12 text-center">
              How It Helps You
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                <div className="w-12 h-12 bg-[#004d8c]/10 text-[#004d8c] rounded-lg flex items-center justify-center mb-6">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-[#004d8c] mb-3">
                  Done-for-you ads (Facebook & Google)
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  We create, run and manage the ads — copy, images, everything.
                  You don't touch it. We use hyper-local targeting to capture
                  people actively looking for a window cleaner.
                </p>
              </div>

              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                <div className="w-12 h-12 bg-[#004d8c]/10 text-[#004d8c] rounded-lg flex items-center justify-center mb-6">
                  <CalendarCheck className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-[#004d8c] mb-3">
                  A system that books customers for you
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Enquiries get an instant quote and are guided to book, day or
                  night, even while you're up the ladder. No more spending your
                  evenings manually quoting jobs.
                </p>
              </div>

              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                <div className="w-12 h-12 bg-[#004d8c]/10 text-[#004d8c] rounded-lg flex items-center justify-center mb-6">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-[#004d8c] mb-3">
                  Automatic follow-up
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Leads that don't book straight away are followed up for you by
                  email and WhatsApp, so good enquiries don't slip through the
                  cracks.
                </p>
              </div>

              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                <div className="w-12 h-12 bg-[#004d8c]/10 text-[#004d8c] rounded-lg flex items-center justify-center mb-6">
                  <BarChart3 className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-[#004d8c] mb-3">
                  Your Marketing CRM
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Every lead, quote and booking in one place, with a live
                  dashboard showing exactly where your money is going and what
                  it's bringing back.
                </p>
              </div>

              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 md:col-span-2">
                <div className="w-12 h-12 bg-[#004d8c]/10 text-[#004d8c] rounded-lg flex items-center justify-center mb-6">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-[#004d8c] mb-3">
                  More than leads — a growth partner
                </h3>
                <p className="text-slate-600 leading-relaxed max-w-3xl">
                  Monthly strategy calls plus ongoing support during the week,
                  from someone who's actually run a window cleaning business and
                  employed cleaners.
                </p>
              </div>
            </div>
            <div className="mt-12 text-center">
              <Button
                onClick={scrollToBooking}
                className="bg-[#fe5240] hover:bg-[#e04825] text-white font-bold text-lg px-10 py-6 rounded-md shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
              >
                Book Your Demo
              </Button>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-24 bg-slate-50 border-y border-slate-100 overflow-hidden">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#004d8c] mb-4">
                Customer Testimonials
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Real results from window cleaning businesses across the UK.
              </p>
            </div>

            <Carousel className="w-full" opts={{ loop: true }}>
              <CarouselContent>
                {/* Lee Pryor */}
                <CarouselItem>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center px-2 md:px-12">
                    <div className="order-2 lg:order-1">
                      <h2 className="text-3xl font-bold tracking-tight text-[#004d8c] sm:text-4xl mb-6">
                        "In March we added £101,000 a year to our round."
                      </h2>
                      <p className="text-lg text-slate-600 mb-8">
                        We partner with ambitious companies like{" "}
                        <strong>Pryor's Window Cleaning</strong> - the largest
                        residential window cleaning company in the UK, based
                        near Gatwick.
                      </p>
                      <div className="bg-white p-6 rounded-2xl border border-slate-200 mb-8 shadow-sm">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-slate-500 font-medium mb-1">
                              Ad Spend
                            </p>
                            <p className="text-3xl font-extrabold text-[#004d8c]">
                              £17,000
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500 font-medium mb-1">
                              Added Revenue
                            </p>
                            <p className="text-3xl font-extrabold text-[#fe5240]">
                              £101k
                              <span className="text-lg font-medium text-slate-500">
                                /yr
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="h-16 w-32 bg-white rounded-sm shadow-md flex items-center justify-center border border-slate-100 overflow-hidden">
                          <img
                            src="https://vibe.filesafe.space/1775166810156919871/attachments/477ac3d4-8e08-48ac-a1e2-ca9ecaf5ee94.png"
                            alt="Pryor's Window Cleaning Logo"
                            width="128"
                            height="64"
                            className="w-full h-full object-contain scale-[1.35]"
                          />
                        </div>
                        <div>
                          <p className="font-bold text-[#004d8c] text-lg">
                            Lee Pryor
                          </p>
                          <p className="text-sm text-slate-500">
                            Owner, Pryor's Window Cleaning
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="order-1 lg:order-2 relative">
                      <div className="absolute inset-0 bg-gradient-to-tr from-[#fe5240]/20 to-[#004d8c]/20 rounded-3xl transform rotate-3"></div>
                      <div className="relative rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-[#004d8c] to-[#004d8c]/80 aspect-video flex flex-col items-center justify-center border border-slate-200">
                        <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center text-white mb-4 backdrop-blur-sm">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-8 h-8 ml-1"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-white tracking-tight">
                          Video Coming Soon
                        </h3>
                        <p className="text-white/80 mt-2 font-medium">
                          Lee's Success Story
                        </p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>

                {/* Robbi Whytock */}
                <CarouselItem>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center px-2 md:px-12">
                    <div className="order-2 lg:order-1">
                      <h2 className="text-3xl font-bold tracking-tight text-[#004d8c] sm:text-4xl mb-6">
                        "Reached a yearly round value of over £250,000."
                      </h2>
                      <p className="text-lg text-slate-600 mb-8">
                        We recently celebrated a massive milestone with{" "}
                        <strong>Robbi Whytock</strong>. By implementing our
                        targeted marketing systems, he's seen incredible growth
                        in just a few months.
                      </p>
                      <div className="bg-white p-6 rounded-2xl border border-slate-200 mb-8 shadow-sm">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-slate-500 font-medium mb-1">
                              Added Since June
                            </p>
                            <p className="text-3xl font-extrabold text-[#fe5240]">
                              £80k
                              <span className="text-lg font-medium text-slate-500">
                                /yr
                              </span>
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500 font-medium mb-1">
                              Total Round Value
                            </p>
                            <p className="text-3xl font-extrabold text-[#004d8c]">
                              £250k+
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="h-16 w-32 bg-white rounded-sm shadow-md flex items-center justify-center border border-slate-100 p-3">
                          <img
                            src="https://vibe.filesafe.space/1775166810156919871/attachments/d862554c-fc11-4e98-8943-ff23500c3ab5.png"
                            alt="Legendary Window Cleaning Logo"
                            width="128"
                            height="64"
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div>
                          <p className="font-bold text-[#004d8c] text-lg">
                            Robbi Whytock
                          </p>
                          <p className="text-sm text-slate-500">
                            Legendary Window Cleaning, Luton
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="order-1 lg:order-2 relative">
                      <div className="absolute inset-0 bg-gradient-to-bl from-[#fe5240]/20 to-[#004d8c]/20 rounded-3xl transform -rotate-3"></div>
                      <div className="relative rounded-3xl overflow-hidden shadow-xl border border-slate-200 aspect-[4/3] sm:aspect-video lg:aspect-auto lg:h-[400px]">
                        <img
                          src="https://vibe.filesafe.space/1775166810156919871/attachments/b147b7df-4634-47d7-8fc9-20402a0d2ddd.jpg"
                          alt="Robbi Whytock receiving his award"
                          loading="lazy"
                          width="800"
                          height="600"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                  </div>
                </CarouselItem>

                {/* Will Farras */}
                <CarouselItem>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center px-2 md:px-12">
                    <div className="order-2 lg:order-1">
                      <h2 className="text-3xl font-bold tracking-tight text-[#004d8c] sm:text-4xl mb-6">
                        "Doubled our round to £160,000 and hired our first
                        employee."
                      </h2>
                      <p className="text-lg text-slate-600 mb-8">
                        <strong>Will Farras</strong> from Brighton-based Farras
                        Window Cleaning has experienced explosive growth,
                        doubling his business in just 6 months.
                      </p>
                      <div className="bg-white p-6 rounded-2xl border border-slate-200 mb-8 shadow-sm">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-slate-500 font-medium mb-1">
                              Round in Sept 2025
                            </p>
                            <p className="text-3xl font-extrabold text-[#004d8c]">
                              £80k
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500 font-medium mb-1">
                              Round in Mar 2026
                            </p>
                            <p className="text-3xl font-extrabold text-[#fe5240]">
                              £160k
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="h-16 w-32 bg-white rounded-sm shadow-md flex items-center justify-center border border-slate-100 p-3">
                          <img
                            src="https://vibe.filesafe.space/1775166810156919871/attachments/abf2c69d-9faf-47e1-9eec-bedfb43b7d11.png"
                            alt="Farras Window Cleaning Logo"
                            width="128"
                            height="64"
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div>
                          <p className="font-bold text-[#004d8c] text-lg">
                            Will Farras
                          </p>
                          <p className="text-sm text-slate-500">
                            Farras Window Cleaning, Brighton
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="order-1 lg:order-2 relative">
                      <div className="absolute inset-0 bg-gradient-to-bl from-[#fe5240]/20 to-[#004d8c]/20 rounded-3xl transform -rotate-3"></div>
                      <div className="relative rounded-3xl overflow-hidden shadow-xl border border-slate-200 aspect-[4/3] sm:aspect-video lg:aspect-auto lg:h-[400px]">
                        <img
                          src="https://vibe.filesafe.space/1775166810156919871/attachments/751503f6-bd4b-450a-ae95-5884cfeebbea.jpg"
                          alt="Will Farras"
                          loading="lazy"
                          width="800"
                          height="600"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              </CarouselContent>

              <div className="hidden md:block">
                <CarouselPrevious className="absolute -left-4 lg:-left-12 top-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-[#fe5240] text-white border-none hover:bg-[#e04825] hover:text-white shadow-lg z-10 opacity-90 hover:opacity-100" />
                <CarouselNext className="absolute -right-4 lg:-right-12 top-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-[#fe5240] text-white border-none hover:bg-[#e04825] hover:text-white shadow-lg z-10 opacity-90 hover:opacity-100" />
              </div>
              <div className="flex items-center justify-center mt-6 md:mt-10">
                <CarouselIndicator />
              </div>
            </Carousel>

            <div className="mt-16 text-center">
              <Button
                onClick={scrollToBooking}
                className="bg-[#fe5240] hover:bg-[#e04825] text-white font-bold text-lg px-10 py-6 rounded-md shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
              >
                Book Your Demo
              </Button>
            </div>
          </div>
        </section>

        {/* Coverage Map Section */}
        <section
          id="coverage"
          className="py-20 md:py-28 bg-white border-t border-slate-100"
        >
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center rounded-full border border-[#004d8c]/20 bg-[#004d8c]/5 px-3 py-1 text-sm font-medium text-[#004d8c] mb-6">
                <MapPin className="w-4 h-4 mr-2" /> Exclusive Territories
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-[#004d8c] sm:text-4xl mb-4">
                We only work with one cleaner per town. Ever.
              </h2>
              <div className="text-lg text-slate-600 max-w-3xl mx-auto space-y-6">
                <p>
                  Unlike our competitors, we do not work with more than one
                  person per town. (We know of one competitor running ads for 3
                  separate companies in 1 town!) We run campaigns on a per-town
                  basis, not a radius. Some cities are split into separate towns
                  based on how people actually search.
                </p>
                <div className="text-left bg-slate-50 p-6 rounded-2xl border border-slate-100 shadow-sm mx-auto max-w-2xl">
                  <p className="font-bold text-[#004d8c] mb-4 text-center sm:text-left">
                    Why do we do this? Because running ads for multiple
                    companies:
                  </p>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-6 w-6 text-[#fe5240] shrink-0 mr-3" />
                      <span>
                        <strong>Dilutes the uniqueness of what we do.</strong>
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-6 w-6 text-[#fe5240] shrink-0 mr-3" />
                      <span>Pushes ad costs up for everyone.</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-6 w-6 text-[#fe5240] shrink-0 mr-3" />
                      <span>
                        Doesn't make you stand out from your competitors.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-6 w-6 text-[#fe5240] shrink-0 mr-3" />
                      <span>
                        Makes acquiring customers more expensive because you're
                        competing against someone with exactly the same setup
                        and advantage.
                      </span>
                    </li>
                  </ul>
                </div>
                <p>
                  Use the tool below to check all of your towns to see if your
                  areas are currently available, or join the waitlist if an area
                  is already taken.
                </p>
              </div>
            </div>
            <div className="max-w-xl mx-auto">
              <AreaChecker />
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section className="py-20 md:py-28 bg-[#004d8c] text-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8">
                  <img
                    src="https://vibe.filesafe.space/1775166810156919871/assets/188e792a-ab1b-4005-94d9-4e25f5a63282.jpg"
                    alt="James Savory"
                    className="w-24 h-24 rounded-full object-cover border-4 border-white/20 shadow-lg shrink-0"
                  />
                  <h2 className="text-3xl md:text-4xl font-bold">
                    We know your business because we've run one.
                  </h2>
                </div>
                <div className="space-y-6 text-lg text-blue-100 leading-relaxed">
                  <p>
                    Clean Marketing was founded by James Savory, a window
                    cleaner who spent over 7 years perfecting these exact
                    systems in-house. He ran his own business, employed
                    cleaners, and spent tens of thousands of his own money
                    figuring out what actually works before offering it to
                    anyone else.
                  </p>
                  <p>
                    We are the <strong>only</strong> agency that works
                    exclusively with window cleaners. We understand the trade,
                    the seasons, the numbers, and the difference between someone
                    searching for "window cleaning spray" and someone wanting a
                    quote.
                  </p>
                  <p>
                    We're honest. We tell you when something isn't working and
                    we don't make excuses. That's the opposite of what burned
                    you before.
                  </p>
                </div>
              </div>
              <div className="bg-white text-slate-900 p-8 rounded-2xl shadow-xl">
                <h3 className="text-2xl font-bold text-[#004d8c] mb-6">
                  What's Included
                </h3>
                <ul className="space-y-4">
                  {[
                    "Managed Facebook & Google ads",
                    "Hyper-local landing pages with a local phone number",
                    "Instant online quoting & booking",
                    "Automated email & WhatsApp follow-up",
                    "Your Marketing CRM with a live financial dashboard",
                    "Review-request automation",
                    "Gutter-clearance reminders",
                    "Monthly strategy meetings & ongoing weekly support",
                    "Planned email marketing campaigns",
                    "A built-in calling system to work your leads fast",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-[#fe5240] mr-3 shrink-0 mt-0.5" />
                      <span className="font-medium text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing & Who This Is For */}
        <section className="py-20 md:py-28 bg-white border-y border-slate-100">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#004d8c] mb-4">
                Simple, transparent pricing.
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                No hidden fees. You know exactly what you're paying and what
                you're getting.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="bg-slate-50 p-8 rounded-2xl border border-slate-200 text-center">
                <h3 className="text-2xl font-bold text-[#004d8c] mb-2">
                  The Growth System
                </h3>
                <p className="text-slate-500 mb-8">
                  Full marketing & automation suite
                </p>

                <div className="flex justify-center items-baseline text-5xl font-extrabold text-[#004d8c] mb-4">
                  <span className="text-xl font-medium text-slate-500 mr-2">
                    From
                  </span>
                  £749
                  <span className="text-xl font-medium text-slate-500 ml-1">
                    /mo +VAT
                  </span>
                </div>

                <div className="space-y-2 mb-8">
                  <p className="text-sm font-medium text-slate-600">
                    Covers up to 10 towns (+£50/mo per additional town)
                  </p>
                  <p className="text-sm font-medium text-slate-600">
                    + £900 +VAT one-time setup fee
                  </p>
                  <p className="text-sm font-bold text-[#fe5240] mt-4">
                    Plus your ad spend (Min. £1,000/month)
                  </p>
                </div>

                <Button
                  onClick={scrollToBooking}
                  className="bg-[#fe5240] hover:bg-[#e04825] text-white font-bold text-lg px-8 py-6 rounded-md shadow-md hover:shadow-lg transition-all w-full"
                >
                  Book Your Demo
                </Button>
              </div>

              <div className="space-y-8">
                <div>
                  <h4 className="text-xl font-bold text-[#004d8c] mb-4 flex items-center">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mr-2" />{" "}
                    This is for you if...
                  </h4>
                  <ul className="space-y-3 text-slate-700">
                    <li className="flex items-start">
                      <span className="mr-2 text-green-500">•</span> Window
                      cleaning is your primary service.
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-500">•</span> You're
                      serious about growing (multiple vans now or soon).
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-500">•</span> You can
                      commit at least £1,000/month to ad spend on top of our
                      fees.
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-green-500">•</span> You
                      understand a customer is worth paying for and you want to
                      do this properly.
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-[#004d8c] mb-4 flex items-center">
                    <span className="w-6 h-6 rounded-full bg-red-100 text-red-500 flex items-center justify-center font-bold mr-2 text-sm">
                      X
                    </span>{" "}
                    This isn't for you if...
                  </h4>
                  <ul className="space-y-3 text-slate-700">
                    <li className="flex items-start">
                      <span className="mr-2 text-red-500">•</span> You're
                      testing the water with a tiny budget.
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-red-500">•</span> You want
                      guaranteed results overnight.
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 text-red-500">•</span> You aren't
                      ready to invest in serious growth.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Risk Reversal */}
        <section className="py-16 bg-[#004d8c]/5 border-y border-[#004d8c]/10">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-[#004d8c] mb-6">
              What if it doesn't work?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <h3 className="font-bold text-[#004d8c] mb-2 text-lg">
                  No Long-Term Traps
                </h3>
                <p className="text-slate-600">
                  We have a 3-month minimum term to give the ads time to
                  establish. After that, it's a rolling contract with just 7
                  days' notice to leave. No being locked in for a year.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <h3 className="font-bold text-[#004d8c] mb-2 text-lg">
                  Proven System
                </h3>
                <p className="text-slate-600">
                  We don't guarantee results overnight — but our system is
                  proven across tens of thousands of bookings, and we're open
                  and honest with you the whole way.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004d8c] mb-12 text-center">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Will this work in my area / for my size of business?",
                  a: "Yes, provided there is enough search volume in your area. We work with everyone from ambitious sole traders to multi-van operations. As long as you have the capacity to take on new work and a minimum £1,000/month ad budget, the system works.",
                },
                {
                  q: "What if it doesn't work for me?",
                  a: "We don't guarantee results, but we do guarantee our system is proven. If it's not working out after the initial 3-month term, you can leave with just 7 days' notice. We don't want to trap anyone in a system that isn't helping them grow.",
                },
                {
                  q: "Do I need to be VAT registered?",
                  a: "No, you don't need to be VAT registered to work with us. However, our management fees and setup fees are subject to VAT.",
                },
                {
                  q: "How much do I need to spend on ads on top of your fees?",
                  a: "We require a minimum ad spend of £1,000 per month. This goes directly to Google and Facebook. Anything less, and the campaigns won't gather enough data to optimise properly.",
                },
                {
                  q: "Am I tied into a long contract?",
                  a: "There is an initial 3-month minimum term to allow the campaigns to optimise. After that, it's a simple rolling contract with 7 days' notice to cancel.",
                },
                {
                  q: "What's actually included for the monthly fee?",
                  a: "Everything you need: managed Google and Facebook ads, dedicated landing pages, the automated quoting and booking system, the Marketing CRM, automated WhatsApp/email follow-ups, and review generation.",
                },
                {
                  q: "How soon will I see results?",
                  a: "Most clients see their first leads within 48 hours of the ads going live. However, campaigns typically take 2-4 weeks to fully optimise and hit their stride.",
                },
                {
                  q: "I've been burned by a marketer before — why are you different?",
                  a: "Because we actually ran a window cleaning business. We didn't learn this in a textbook; we spent our own money figuring out what works. We don't over-promise, we don't use generic templates, and we are completely transparent with your data.",
                },
              ].map((faq, i) => (
                <div
                  key={i}
                  className="bg-slate-50 p-6 rounded-xl border border-slate-100"
                >
                  <h3 className="font-bold text-[#004d8c] text-lg mb-2">
                    {faq.q}
                  </h3>
                  <p className="text-slate-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 bg-[#004d8c] text-white text-center">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Ready to stop wasting money and start filling your vans?
            </h2>
            <p className="text-xl text-blue-100 mb-10">
              Book a no-pressure demo to see exactly how our system would work
              for your business.
            </p>
            <Button
              onClick={scrollToBooking}
              className="bg-[#fe5240] hover:bg-[#e04825] text-white font-bold text-lg px-12 py-8 rounded-md shadow-xl hover:shadow-2xl transition-all w-full sm:w-auto"
            >
              Book Your Demo
            </Button>
          </div>
        </section>

        {/* Booking Section */}
        <section id="booking-section" className="py-24 bg-slate-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#004d8c] mb-4">
                Ready to fill your vans?
              </h2>
              <p className="text-lg text-slate-600">
                Complete the quick form below to see if you qualify, then pick a
                time for your demo.
              </p>
            </div>
            <div className="bg-white p-6 md:p-10 rounded-2xl shadow-xl border border-slate-200">
              <PreQualForm onComplete={() => {}} />
            </div>
          </div>
        </section>
      </main>

      {/* Minimal Footer */}
      <footer className="py-12 bg-slate-900 text-slate-400 text-center">
        <div className="container mx-auto px-4">
          <img
            src="https://vibe.filesafe.space/1775166810156919871/attachments/07b67583-88fd-417b-bb44-aeaaee1d2dda.png"
            alt="Clean Marketing Logo"
            className="h-8 object-contain mx-auto mb-6 brightness-0 invert opacity-50"
          />
          <p className="mb-4">
            © {new Date().getFullYear()} Clean Marketing. All rights reserved.
          </p>
          <p className="text-sm max-w-2xl mx-auto">
            This site is not a part of the Facebook website or Facebook Inc.
            Additionally, This site is NOT endorsed by Facebook in any way.
            FACEBOOK is a trademark of FACEBOOK, Inc.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
